import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  useDesign,
  type BuildTool,
  isUnstartedRoom,
  DEFAULT_PRESET_ROOM_WIDTH_IN,
  DEFAULT_PRESET_ROOM_DEPTH_IN,
} from "../../lib/store";
import { Stat } from "../ui/Stat";
import {
  boundingBox,
  distance,
  formatArea,
  formatLength,
  polygonArea,
  polygonPerimeter,
  presetShape,
  resizeEdgeWithLocks,
} from "../../lib/geom";
import type { RoomShapeKind } from "../../lib/types";
import {
  ArrowRight,
  BedDouble,
  ChevronDown,
  CornerUpRight,
  Lock,
  Move,
  PencilLine,
  Plus,
  RotateCcw,
  Shapes,
  Trash2,
  Unlock,
  X,
} from "lucide-react";
import { RoomEditorCanvas } from "../canvas/RoomEditorCanvas";
import type { SketchOutcome } from "../canvas/SketchOverlay";
import { Button } from "../ui/Button";
import { NumberStepper } from "../ui/NumberStepper";
import { Sheet } from "../ui/Sheet";
import { MinimizableInspector } from "../ui/MinimizableInspector";
import { ContextMenu, type ContextMenuPosition } from "../ui/ContextMenu";
import {
  DesktopSidePanel,
  SelectionPanelHeader,
} from "../ui/ContextSidePanel";
import { useCoarsePointer, useIsMobile, useLayoutMode } from "../../lib/useViewportSize";
import { useTopbarSlot } from "../../lib/topbarSlot";
import { requestRoomTemplates } from "../../lib/roomTemplateRequests";
import { activeOnboardingFlow } from "../../lib/onboardingFlow";
import { track } from "../../lib/telemetry";
import { SketchConfirmBar } from "./SketchConfirmBar";
import { RoomFinishesRow } from "../finishes/RoomFinishesRow";
import { RoomSettingControl } from "../finishes/RoomSettingControl";
import { roomSetting } from "../../lib/finishes/effective";
import { ROOM_FINISHES_FLAG, useFlag } from "../../lib/flags";
import {
  addInteriorCorner,
  canRemoveBoundaryVertex,
  clearInteriorGeometry,
  commitInteriorWall,
  deleteInteriorWall,
  editableWalls,
  growOutline,
  interiorLockKey,
  interiorSegmentCount,
  removeBoundaryVertex,
  removeInteriorVertex,
  resizeInteriorSegment,
  splitBoundaryEdge,
  wallLabel,
} from "../../lib/interiorWalls";
import { cn } from "../../lib/cn";
import type { Unit } from "../../lib/types";

/** Stable empty-locks object so the `lockedWallLengths` selector returns a
 *  referentially identical value when the field is absent, preventing React
 *  19's `useSyncExternalStore` from tripping on a fresh `{}` literal each
 *  render. */
const EMPTY_LOCKS: Record<number, number> = Object.freeze({});

/** The tool row and floating buttons that sit over the canvas edges. */
const CANVAS_CHROME = {
  top: "var(--canvas-inset-top)",
  bottom: "var(--canvas-inset-bottom)",
} as const;
/** Same, while a hint chip floats under the tool row (see `--hint-row-h`). */
const CANVAS_CHROME_WITH_HINT = {
  top: "calc(var(--canvas-inset-top) + var(--hint-row-h))",
  bottom: "var(--canvas-inset-bottom)",
} as const;

const SHAPE_OPTIONS: { id: RoomShapeKind; label: string; preview: React.ReactNode }[] = [
  {
    id: "rectangle",
    label: "Rectangle",
    preview: (
      <svg viewBox="0 0 40 32" className="size-full">
        <rect x="4" y="6" width="32" height="20" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    id: "l-shape",
    label: "L-shape",
    preview: (
      <svg viewBox="0 0 40 32" className="size-full">
        <path d="M4 6 L24 6 L24 16 L36 16 L36 26 L4 26 Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "u-shape",
    label: "U-shape",
    preview: (
      <svg viewBox="0 0 40 32" className="size-full">
        <path d="M4 6 L12 6 L12 18 L28 18 L28 6 L36 6 L36 26 L4 26 Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "t-shape",
    label: "T-shape",
    preview: (
      <svg viewBox="0 0 40 32" className="size-full">
        <path d="M4 6 L36 6 L36 14 L25 14 L25 26 L15 26 L15 14 L4 14 Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export function BuildView() {
  const room = useDesign((s) => s.design.room);
  const unit = useDesign((s) => s.design.unit);
  const setRoomPoints = useDesign((s) => s.setRoomPoints);
  const applyRoomChange = useDesign((s) => s.applyRoomChange);
  const openings = useDesign((s) => s.design.openings);
  const mounts = useDesign((s) => s.design.mounts);
  const setCeilingHeight = useDesign((s) => s.setCeilingHeight);
  const setView = useDesign((s) => s.setView);
  const resetAll = useDesign((s) => s.resetAll);
  const selectedWall = useDesign((s) => s.selectedRoomWallIndex);
  const selectedVertex = useDesign((s) => s.selectedRoomVertexIndex);
  const selectRoomWall = useDesign((s) => s.selectRoomWall);
  const selectRoomVertex = useDesign((s) => s.selectRoomVertex);
  const selectedInteriorWall = useDesign((s) => s.selectedInteriorWall);
  const selectedInteriorVertexId = useDesign((s) => s.selectedInteriorVertexId);
  const selectInteriorWall = useDesign((s) => s.selectInteriorWall);
  const selectInteriorVertex = useDesign((s) => s.selectInteriorVertex);
  const lockInteriorSegment = useDesign((s) => s.lockInteriorSegment);
  const unlockInteriorSegment = useDesign((s) => s.unlockInteriorSegment);
  const buildTool = useDesign((s) => s.buildTool);
  const setBuildTool = useDesign((s) => s.setBuildTool);
  const pendingSketch = useDesign((s) => s.pendingSketch);
  const setPendingSketch = useDesign((s) => s.setPendingSketch);
  const lockedWallLengths = useDesign(
    (s) => s.design.room.lockedWallLengths ?? EMPTY_LOCKS
  );
  const lockWall = useDesign((s) => s.lockWall);
  const unlockWall = useDesign((s) => s.unlockWall);
  const batch = useDesign((s) => s.batch);

  const isMobile = useIsMobile();
  const coarse = useCoarsePointer();
  const topbarSlot = useTopbarSlot();
  const toolsInTopbar = useLayoutMode() === "phoneLandscape" && topbarSlot != null;

  const [shapesOpen, setShapesOpen] = useState(false);
  const [ceilingOpen, setCeilingOpen] = useState(false);
  const finishesOn = useFlag(ROOM_FINISHES_FLAG);
  const outdoor = roomSetting(room.finishes) === "outdoor";
  /** Anchor of the mobile "Add" menu (addition / interior wall). */
  const [addMenu, setAddMenu] = useState<ContextMenuPosition | null>(null);

  /** Phone tool row (Start over, Add, room details). On landscape phones
   *  it sits in the top bar at the standard 44px height. */
  function phoneTools(inTopbar: boolean) {
    if (!roomStarted) return null;
    const h = inTopbar ? "h-11" : "h-12";
    return (
      <>
        <button
          type="button"
          onClick={startOver}
          className={cn(
            "pointer-events-auto shrink-0 px-3 rounded-2xl bg-white ring-1 ring-ink-200 inline-flex items-center gap-1.5 text-body-sm font-medium text-ink-700 shadow-sm active:bg-ink-100",
            h
          )}
          data-testid="build-start-over"
        >
          <RotateCcw className="size-4" />
          Start over
        </button>
        <button
          type="button"
          onClick={(e) => {
            if (extendMode) {
              cancelExtendSketch();
              return;
            }
            const r = e.currentTarget.getBoundingClientRect();
            setAddMenu({ x: r.left, y: r.bottom + 6 });
          }}
          aria-haspopup={extendMode ? undefined : "menu"}
          aria-expanded={extendMode ? undefined : addMenu !== null}
          className={cn(
            "pointer-events-auto shrink-0 px-3 rounded-2xl ring-1 inline-flex items-center gap-1.5 text-body-sm font-medium transition-colors shadow-sm",
            h,
            extendMode
              ? "bg-ink-900 text-white ring-ink-900"
              : "bg-white ring-ink-200 text-ink-700 active:bg-ink-100"
          )}
          data-testid="build-add-menu-button"
        >
          {extendMode ? (
            <>
              <X className="size-4" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="size-4" />
              Add
              <ChevronDown className="size-3.5 opacity-70" />
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => setCeilingOpen(true)}
          className={cn(
            "pointer-events-auto shrink-0 px-3 rounded-2xl bg-white ring-1 ring-ink-200 inline-flex items-center gap-1.5 text-body-sm font-semibold text-ink-900 tabular-nums shadow-sm active:bg-ink-100",
            h,
            !inTopbar && "ml-auto"
          )}
          data-testid="build-room-details-button"
        >
          {formatArea(area, unit)}
        </button>
      </>
    );
  }
  /** Hide the blank-canvas chooser once the user picks sketch or a preset. */
  const [startupDismissed, setStartupDismissed] = useState(false);
  /** User dismissed the Edit-mode "drag walls" tip for this session. */
  const [editTipDismissed, setEditTipDismissed] = useState(false);
  /** True while one sketch can become either an addition or an interior wall. */
  const [extendMode, setExtendMode] = useState(false);
  /** Result of the most recent stroke — drives the confirm bar. */
  const [sketchStatus, setSketchStatus] = useState<{
    ok: boolean;
    message: string;
    appendMode: boolean;
    wallMode: boolean;
  } | null>(null);

  /** Wrapper around `setBuildTool` that also resets our addition flag and
   *  confirm-bar status when leaving Sketch mode. Centralizing the reset
   *  here lets us avoid a `useEffect` (forbidden by `react-hooks/
   *  set-state-in-effect` for cascading renders) — every caller that
   *  changes the tool goes through this helper. */
  function applyBuildTool(t: BuildTool) {
    setExtendMode(false);
    if (t !== "sketch") setSketchStatus(null);
    setBuildTool(t);
  }

  const bb = useMemo(() => boundingBox(room.points), [room.points]);
  const area = polygonArea(room.points);
  const perim = polygonPerimeter(room.points);
  const roomStarted = !isUnstartedRoom(room);
  const showStartupOverlay =
    !roomStarted &&
    !startupDismissed &&
    !sketchStatus &&
    !pendingSketch &&
    !shapesOpen;
  const hintShown =
    (buildTool === "sketch" && !sketchStatus && !showStartupOverlay) ||
    (roomStarted &&
      buildTool === "edit" &&
      selectedWall == null &&
      selectedVertex == null &&
      selectedInteriorWall == null &&
      selectedInteriorVertexId == null &&
      !editTipDismissed);

  function applyPreset(kind: RoomShapeKind) {
    if (kind === "custom") return;
    const points = presetShape(
      kind as Exclude<RoomShapeKind, "custom">,
      roomStarted ? bb.width || DEFAULT_PRESET_ROOM_WIDTH_IN : DEFAULT_PRESET_ROOM_WIDTH_IN,
      roomStarted ? bb.height || DEFAULT_PRESET_ROOM_DEPTH_IN : DEFAULT_PRESET_ROOM_DEPTH_IN
    );
    // Preset geometry replaces every wall — any lock values the user had on
    // the old layout no longer correspond to the new walls, so drop them.
    const cleared = clearInteriorGeometry(room, openings, mounts, points);
    const nextRoom = { ...cleared.room, shapeKind: kind };
    delete nextRoom.lockedWallLengths;
    applyRoomChange(nextRoom, cleared.openings, cleared.mounts);
    selectRoomWall(null);
    selectRoomVertex(null);
    setShapesOpen(false);
    setStartupDismissed(true);
    applyBuildTool("edit");
  }

  const walls = useMemo(() => editableWalls(room), [room]);
  const interiorSelection = selectedInteriorWall
    ? walls.find(
        (w) =>
          w.kind === "interior" &&
          w.wallId === selectedInteriorWall.wallId &&
          w.segment === selectedInteriorWall.segment
      ) ?? null
    : null;

  function clearSelection() {
    selectRoomWall(null);
    selectRoomVertex(null);
    selectInteriorWall(null);
    selectInteriorVertex(null);
  }

  function addCornerToSelectedWall() {
    if (selectedInteriorWall) {
      const added = addInteriorCorner(
        room,
        openings,
        mounts,
        selectedInteriorWall.wallId,
        selectedInteriorWall.segment
      );
      if (!added) return;
      applyRoomChange(added.room, added.openings, added.mounts);
      selectInteriorVertex(added.vertexId);
      return;
    }
    if (selectedWall == null) return;
    const split = splitBoundaryEdge(room, openings, mounts, selectedWall, 0.5);
    if (!split) return;
    applyRoomChange(split.room, split.openings, split.mounts);
    selectRoomVertex(split.vertexIndex);
  }

  function removeSelectedVertex() {
    if (selectedInteriorVertexId) {
      const removed = removeInteriorVertex(room, openings, mounts, selectedInteriorVertexId);
      if (!removed) return;
      applyRoomChange(removed.room, removed.openings, removed.mounts);
      clearSelection();
      return;
    }
    if (selectedVertex == null) return;
    const removed = removeBoundaryVertex(room, openings, mounts, selectedVertex);
    if (!removed) return;
    applyRoomChange(removed.room, removed.openings, removed.mounts);
    clearSelection();
  }

  function setSelectedWallLength(newLen: number) {
    if (selectedInteriorWall) {
      const { wallId, segment } = selectedInteriorWall;
      batch(() => {
        applyRoomChange(resizeInteriorSegment(room, wallId, segment, newLen));
        lockInteriorSegment(wallId, segment, newLen);
      });
      return;
    }
    if (selectedWall == null) return;
    const wall = selectedWall;
    batch(() => {
      setRoomPoints(
        resizeEdgeWithLocks(room.points, wall, newLen, lockedWallLengths)
      );
      lockWall(wall, newLen);
    });
  }

  const selectedWallLen = useMemo(() => {
    if (interiorSelection) return interiorSelection.len;
    if (selectedWall == null) return 0;
    const a = room.points[selectedWall];
    const b = room.points[(selectedWall + 1) % room.points.length];
    return distance(a, b);
  }, [interiorSelection, selectedWall, room.points]);

  const selectedWallLocked = interiorSelection
    ? interiorLockKey(interiorSelection.wallId!, interiorSelection.segment!) in
      (room.lockedInteriorLengths ?? {})
    : selectedWall != null && selectedWall in lockedWallLengths;

  function toggleSelectedWallLock() {
    if (interiorSelection?.wallId != null && interiorSelection.segment != null) {
      const key = interiorLockKey(interiorSelection.wallId, interiorSelection.segment);
      if (key in (room.lockedInteriorLengths ?? {})) {
        unlockInteriorSegment(interiorSelection.wallId, interiorSelection.segment);
      } else {
        lockInteriorSegment(
          interiorSelection.wallId,
          interiorSelection.segment,
          selectedWallLen
        );
      }
      return;
    }
    if (selectedWall == null) return;
    if (selectedWall in lockedWallLengths) {
      unlockWall(selectedWall);
    } else {
      lockWall(selectedWall, selectedWallLen);
    }
  }

  function deleteSelectedInteriorWall() {
    if (!selectedInteriorWall) return;
    const deleted = deleteInteriorWall(
      room,
      openings,
      mounts,
      selectedInteriorWall.wallId
    );
    applyRoomChange(deleted.room, deleted.openings, deleted.mounts);
    clearSelection();
  }

  const selectionLabel = interiorSelection
    ? `Wall ${wallLabel(interiorSelection.labelIndex)}`
    : selectedInteriorVertexId
      ? "Corner"
      : selectedWall != null
        ? `Wall ${wallLabel(selectedWall)}`
        : selectedVertex != null
          ? `Corner ${selectedVertex + 1}`
          : null;

  function handleSketchResult(outcome: SketchOutcome) {
    setPendingSketch(outcome.ok ? outcome.points : null);
    setSketchStatus({
      ok: outcome.ok,
      message: outcome.message,
      appendMode: outcome.kind === "addition",
      wallMode: outcome.kind === "wall",
    });
  }

  function commitPendingSketch() {
    if (!pendingSketch || !sketchStatus) return;
    if (sketchStatus.wallMode) {
      const joined = commitInteriorWall(room, pendingSketch, openings, mounts);
      if (!joined.ok) {
        setPendingSketch(null);
        setSketchStatus({ ok: false, message: joined.message, appendMode: false, wallMode: true });
        return;
      }
      applyRoomChange(joined.room, joined.openings, joined.mounts);
      setPendingSketch(null);
      setStartupDismissed(true);
      applyBuildTool("edit");
      selectInteriorWall({ wallId: joined.wallId, segment: 0 });
      return;
    }
    const replaced = sketchStatus.appendMode
      ? growOutline(room, openings, mounts, pendingSketch)
      : clearInteriorGeometry(room, openings, mounts, pendingSketch);
    applyRoomChange({ ...replaced.room, shapeKind: "custom" }, replaced.openings, replaced.mounts);
    setPendingSketch(null);
    setStartupDismissed(true);
    applyBuildTool("edit");
  }

  function tweakPendingSketch() {
    commitPendingSketch();
  }

  function redrawPendingSketch() {
    setPendingSketch(null);
    setSketchStatus(null);
  }

  function startExtendSketch() {
    setSketchStatus(null);
    setPendingSketch(null);
    setExtendMode(true);
    setBuildTool("sketch");
  }

  function cancelExtendSketch() {
    setPendingSketch(null);
    setAddMenu(null);
    applyBuildTool("edit");
  }

  function startOver() {
    if (!confirm("Clear this room and start fresh?")) return;
    setStartupDismissed(false);
    setEditTipDismissed(false);
    setExtendMode(false);
    setShapesOpen(false);
    setAddMenu(null);
    setPendingSketch(null);
    resetAll();
  }

  function beginSketchFromScratch() {
    const flow = activeOnboardingFlow();
    if (flow) track("room_sketch_started", { flow });
    setStartupDismissed(true);
    setExtendMode(false);
    setBuildTool("sketch");
  }

  const boundaryCornerBlocked =
    selectedVertex != null && !canRemoveBoundaryVertex(room, selectedVertex);
  const selectionOpen =
    buildTool === "edit" &&
    (selectedWall != null ||
      selectedVertex != null ||
      selectedInteriorWall != null ||
      selectedInteriorVertexId != null);

  return (
    <div className="relative flex h-full">
      <main className="flex-1 relative bg-ink-50 min-h-0">
        <div className="absolute inset-x-0 top-0 bottom-[var(--canvas-bottom)]">
          <RoomEditorCanvas
            hideAreaBadge
            tool={buildTool}
            extendMode={extendMode}
            previewClosed={!sketchStatus?.wallMode}
            previewPoints={pendingSketch}
            onSketchResult={handleSketchResult}
            fitInsets={hintShown ? CANVAS_CHROME_WITH_HINT : CANVAS_CHROME}
          />
        </div>

        <div className="absolute left-3 right-3 md:left-6 md:right-6 top-[calc(var(--topbar-h)+0.5rem)] z-10 flex flex-col gap-3 pointer-events-none">
          {isMobile ? (
            toolsInTopbar ? (
              createPortal(phoneTools(true), topbarSlot)
            ) : (
              <div className="flex items-center gap-2">{phoneTools(false)}</div>
            )
          ) : (
            <>
          <div className="flex items-start gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1 md:mx-0 md:px-0 md:overflow-visible">
            <Stat
              label="Area"
              value={roomStarted ? formatArea(area, unit) : "—"}
              className="pointer-events-auto shrink-0"
            />
            <Stat
              label="Perimeter"
              value={roomStarted ? formatLength(perim, unit) : "—"}
              className="pointer-events-auto shrink-0"
            />
            <Stat
              label="Walls"
              value={
                roomStarted
                  ? String(room.points.length + interiorSegmentCount(room))
                  : "—"
              }
              className="pointer-events-auto shrink-0"
            />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1 md:mx-0 md:px-0 md:overflow-visible">
            {roomStarted && (
              <button
                type="button"
                onClick={startOver}
                className="pointer-events-auto shrink-0 h-10 px-3 rounded-2xl bg-white ring-1 ring-ink-200 inline-flex items-center gap-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 active:bg-ink-100 transition-colors shadow-sm"
                data-testid="build-start-over"
              >
                <RotateCcw className="size-4" />
                <span>Start over</span>
              </button>
            )}
            {roomStarted && (
              <button
                type="button"
                onClick={extendMode ? cancelExtendSketch : startExtendSketch}
                className={cn(
                  "pointer-events-auto shrink-0 h-10 px-3 rounded-2xl ring-1 inline-flex items-center gap-1.5 text-xs font-medium transition-colors shadow-sm",
                  extendMode
                    ? "bg-ink-900 text-white ring-ink-900"
                    : "bg-white ring-ink-200 text-ink-700 hover:bg-ink-50 active:bg-ink-100"
                )}
              >
                {extendMode ? <X className="size-4" /> : <PencilLine className="size-4" />}
                <span>{extendMode ? "Cancel" : "Add"}</span>
              </button>
            )}
            <button
              onClick={() => setCeilingOpen(true)}
              className="pointer-events-auto shrink-0 h-10 px-3 rounded-2xl bg-white ring-1 ring-ink-200 inline-flex items-center gap-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 active:bg-ink-100 transition-colors shadow-sm"
            >
              <span className="text-ink-400">{outdoor ? "Overhead" : "Ceiling"}</span>
              <span className="font-semibold">{formatLength(room.ceilingHeight, unit)}</span>
            </button>
          </div>
            </>
          )}

          {showStartupOverlay && (
            <div className="flex justify-center pt-6 md:pt-10">
              <div className="pointer-events-auto bg-white/95 backdrop-blur ring-1 ring-ink-200 rounded-2xl px-5 py-4 shadow-lg max-w-sm text-center">
                <div className="text-sm font-semibold text-ink-900">
                  Start your room
                </div>
                <div className="text-xs text-ink-500 mt-1">
                  Sketch an outline on the grid, pick a preset shape, or start from a furnished bedroom.
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={beginSketchFromScratch}
                    className="flex flex-col items-center gap-2 p-4 rounded-2xl ring-1 ring-ink-200 bg-white text-ink-700 hover:ring-accent-400 hover:bg-accent-50 transition-all"
                  >
                    <PencilLine className="size-6 text-accent-600" />
                    <span className="text-sm font-medium">Sketch</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const flow = activeOnboardingFlow();
                      if (flow) track("room_shapes_opened", { flow });
                      setShapesOpen(true);
                    }}
                    className="flex flex-col items-center gap-2 p-4 rounded-2xl ring-1 ring-ink-200 bg-white text-ink-700 hover:ring-accent-400 hover:bg-accent-50 transition-all"
                  >
                    <Shapes className="size-6 text-accent-600" />
                    <span className="text-sm font-medium">Pick a shape</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const flow = activeOnboardingFlow();
                      if (flow) track("room_templates_opened", { flow });
                      requestRoomTemplates();
                    }}
                    className="flex flex-col items-center gap-2 p-4 rounded-2xl ring-1 ring-ink-200 bg-white text-ink-700 hover:ring-accent-400 hover:bg-accent-50 transition-all"
                    data-testid="start-room-template"
                  >
                    <BedDouble className="size-6 text-accent-600" />
                    <span className="text-sm font-medium">Bedroom</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Contextual hint card. Hidden while a wall/vertex is selected
              (in Edit mode) or while a confirm bar is showing. */}
          {buildTool === "sketch" && !sketchStatus && !showStartupOverlay && !addMenu && (
            <div className="flex justify-center">
              <div className="bg-white/95 backdrop-blur ring-1 ring-ink-200 rounded-2xl px-4 py-2.5 shadow-sm max-w-sm text-center pointer-events-none">
                <div className="text-xs font-semibold text-ink-800 text-balance">
                  {extendMode
                    ? "Draw a wall inside the room, or outline an addition against a wall."
                    : "Draw the outline of your room"}
                </div>
                {/* Landscape phones keep just the one-line title. */}
                <div className="text-xs text-ink-500 mt-0.5 land:hidden">
                  {isMobile
                    ? "Drag your finger to draw — Room Canvas snaps to clean walls when you lift."
                    : "Hold Shift for free angles. Drag to draw — Room Canvas snaps to clean walls when you lift."}
                </div>
              </div>
            </div>
          )}

          {roomStarted &&
            buildTool === "edit" &&
            selectedWall == null &&
            selectedVertex == null &&
            selectedInteriorWall == null &&
            selectedInteriorVertexId == null &&
            !editTipDismissed &&
            !addMenu && (
            <div className="flex justify-center">
              <div className="relative pointer-events-auto bg-white/95 backdrop-blur ring-1 ring-ink-200 rounded-2xl pl-4 pr-11 py-2.5 land:py-0 land:min-h-11 land:flex land:items-center land:rounded-full shadow-sm max-w-sm text-left">
                <button
                  type="button"
                  onClick={() => setEditTipDismissed(true)}
                  aria-label="Dismiss tip"
                  className="absolute top-0 right-0 h-11 w-11 inline-flex items-center justify-center rounded-2xl land:rounded-full text-ink-400 hover:text-ink-600 active:bg-ink-100 transition-colors"
                >
                  <X className="size-4" />
                </button>
                <div className="text-body-sm font-semibold text-ink-800">
                  {coarse ? "Hold walls & corners to reshape your room" : "Drag walls & corners to shape your room"}
                </div>
                {/* Landscape phones: a one-line dismissible chip. */}
                <div className="text-xs text-ink-500 mt-0.5 land:hidden">
                  {isMobile
                    ? "Tap a wall to set its exact length. Use Add for alcoves and interior walls."
                    : "Tap a wall to set its exact length, or use Add for alcoves and interior walls."}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile selection editor — desktop uses the right context panel. */}
        {isMobile && (
          <MinimizableInspector
            open={selectionOpen}
            onClose={clearSelection}
            itemId={
              selectedInteriorWall
                ? `iwall-${selectedInteriorWall.wallId}-${selectedInteriorWall.segment}`
                : selectedInteriorVertexId
                  ? `ivertex-${selectedInteriorVertexId}`
                  : selectedWall != null
                    ? `wall-${selectedWall}`
                    : selectedVertex != null
                      ? `vertex-${selectedVertex}`
                      : null
            }
            title={selectionLabel ?? ""}
            meta={
              selectedWall != null || interiorSelection
                ? formatLength(selectedWallLen, unit)
                : selectedVertex != null || selectedInteriorVertexId
                  ? "Drag to reshape"
                  : undefined
            }
            swatch={
              <div className="size-7 inline-flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                {selectedWall != null || interiorSelection ? (
                  <span className="text-xs font-bold">
                    {wallLabel(interiorSelection?.labelIndex ?? selectedWall ?? 0)}
                  </span>
                ) : (
                  <CornerUpRight className="size-4" />
                )}
              </div>
            }
          >
            <BuildSelectionContent
              selectedWall={selectedWall}
              selectedVertex={selectedVertex}
              selectedWallLen={selectedWallLen}
              selectedWallLocked={selectedWallLocked}
              unit={unit}
              roomPointCount={room.points.length}
              onSetWallLength={setSelectedWallLength}
              onToggleWallLock={toggleSelectedWallLock}
              onAddCorner={addCornerToSelectedWall}
              onRemoveVertex={removeSelectedVertex}
              forceWall={!!interiorSelection}
              forceCorner={!!selectedInteriorVertexId}
              canRemoveCorner={
                selectedInteriorVertexId
                  ? true
                  : selectedVertex != null && !boundaryCornerBlocked
              }
              cornerHint={
                boundaryCornerBlocked
                  ? "Delete the interior wall before removing this corner"
                  : undefined
              }
              onDeleteWall={interiorSelection ? deleteSelectedInteriorWall : undefined}
            />
          </MinimizableInspector>
        )}

        {/* Sketch tool's confirm bar (Use / Tweak / Redraw). Sits above the
            standard "Add furniture" CTA whenever there is a pending sketch. */}
        <SketchConfirmBar
          status={sketchStatus}
          onConfirm={commitPendingSketch}
          onRedraw={redrawPendingSketch}
          onTweak={tweakPendingSketch}
        />

        {/* Bottom CTA (mobile + desktop overlay). Hidden while the user is
            in the middle of a sketch confirm to keep the bottom area clean. */}
        {!sketchStatus && roomStarted && (
          <div className="absolute left-0 right-0 bottom-0 z-10 pointer-events-none">
            {/* The selection bar takes this row while a wall or corner is selected. */}
            <div
              className={cn(
                "md:hidden absolute right-[calc(0.75rem+var(--safe-right))] bottom-[var(--float-bottom)]",
                selectionOpen && "hidden"
              )}
            >
              <Button
                variant="primary"
                size="lg"
                className="pointer-events-auto shadow-lg shadow-ink-900/20"
                onClick={() => setView("design")}
              >
                Add furniture
                <ArrowRight className="size-4" />
              </Button>
            </div>
            <div className="hidden md:flex md:justify-end md:px-6 md:pb-6">
              <Button
                variant="primary"
                size="lg"
                className="pointer-events-auto shadow-lg shadow-ink-900/15"
                onClick={() => setView("design")}
              >
                <Move className="size-4" />
                Add furniture
                <ArrowRight className="size-4" />
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Desktop context panel — only appears when editing a wall or corner */}
      {buildTool === "edit" &&
        (selectedWall != null ||
          selectedVertex != null ||
          selectedInteriorWall != null ||
          selectedInteriorVertexId != null) && (
        <DesktopSidePanel>
          <SelectionPanelHeader
            title={selectionLabel ?? ""}
            meta={
              selectedWall != null || interiorSelection
                ? formatLength(selectedWallLen, unit)
                : selectedVertex != null || selectedInteriorVertexId
                  ? "Drag to reshape"
                  : undefined
            }
            swatch={
              <div className="size-7 inline-flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                {selectedWall != null || interiorSelection ? (
                  <span className="text-xs font-bold">
                    {wallLabel(interiorSelection?.labelIndex ?? selectedWall ?? 0)}
                  </span>
                ) : (
                  <CornerUpRight className="size-4" />
                )}
              </div>
            }
            onClose={clearSelection}
            eyebrow="Edit room"
          />
          <div className="flex-1 overflow-y-auto min-h-0">
            <BuildSelectionContent
              selectedWall={selectedWall}
              selectedVertex={selectedVertex}
              selectedWallLen={selectedWallLen}
              selectedWallLocked={selectedWallLocked}
              unit={unit}
              roomPointCount={room.points.length}
              onSetWallLength={setSelectedWallLength}
              onToggleWallLock={toggleSelectedWallLock}
              onAddCorner={addCornerToSelectedWall}
              onRemoveVertex={removeSelectedVertex}
              forceWall={!!interiorSelection}
              forceCorner={!!selectedInteriorVertexId}
              canRemoveCorner={
                selectedInteriorVertexId
                  ? true
                  : selectedVertex != null && !boundaryCornerBlocked
              }
              cornerHint={
                boundaryCornerBlocked
                  ? "Delete the interior wall before removing this corner"
                  : undefined
              }
              onDeleteWall={interiorSelection ? deleteSelectedInteriorWall : undefined}
            />
          </div>
        </DesktopSidePanel>
      )}

      {/* Shape preset sheet */}
      <Sheet
        open={shapesOpen}
        onClose={() => setShapesOpen(false)}
        title="Start with a shape"
      >
        <p className="text-sm text-ink-500 mb-3">
          Pick a preset to place on the grid — you can still drag any wall or corner afterwards.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {SHAPE_OPTIONS.map((opt) => {
            const active = room.shapeKind === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => applyPreset(opt.id)}
                className={cn(
                  "flex flex-col items-center gap-2 p-4 rounded-2xl ring-1 transition-all",
                  active
                    ? "ring-ink-900 bg-ink-900 text-white"
                    : "ring-ink-200 bg-white text-ink-700 hover:ring-ink-400"
                )}
              >
                <div className="h-14 w-full">{opt.preview}</div>
                <div className="text-sm font-medium">{opt.label}</div>
              </button>
            );
          })}
        </div>
      </Sheet>

      <ContextMenu
        position={addMenu}
        onClose={() => setAddMenu(null)}
        ariaLabel="Add to room"
        items={[
          {
            id: "extend",
            label: "Wall or addition",
            icon: PencilLine,
            onSelect: startExtendSketch,
          },
        ]}
      />

      {/* Room details: stats, ceiling height and finishes. The phone
          toolbar's room chip and the desktop Ceiling button both open it. */}
      <Sheet
        open={ceilingOpen}
        onClose={() => setCeilingOpen(false)}
        title={isMobile || finishesOn ? "Room details" : "Ceiling height"}
      >
        {isMobile && roomStarted && (
          <div className="grid grid-cols-3 gap-2 mb-5" data-testid="room-details-stats">
            <Stat label="Area" value={formatArea(area, unit)} />
            <Stat label="Perimeter" value={formatLength(perim, unit)} />
            <Stat
              label={outdoor ? "Sides" : "Walls"}
              value={String(room.points.length + interiorSegmentCount(room))}
            />
          </div>
        )}
        {roomStarted && <RoomSettingControl />}
        <h4 className="text-body-sm font-semibold text-ink-900 mb-1">{outdoor ? "Overhead height" : "Ceiling height"}</h4>
        <p className="text-sm text-ink-500 mb-4">
          {outdoor
            ? "How high a pergola or roof sits, and the neighbor's house wall. Fences and hedges keep their own height."
            : "Used when you mount things on walls to make sure heights are realistic."}
        </p>
        <div className="flex items-center gap-3">
          <NumberStepper
            label="Height"
            value={room.ceilingHeight}
            onChange={setCeilingHeight}
            min={84}
            max={240}
            step={3}
            suffix="in"
          />
          <div className="text-xs text-ink-400">≈ {formatLength(room.ceilingHeight, unit)}</div>
        </div>
        {roomStarted && <RoomFinishesRow onOpen={() => setCeilingOpen(false)} />}
      </Sheet>
    </div>
  );
}

function BuildSelectionContent({
  selectedWall,
  selectedVertex,
  selectedWallLen,
  selectedWallLocked,
  unit,
  roomPointCount,
  onSetWallLength,
  onToggleWallLock,
  onAddCorner,
  onRemoveVertex,
  forceWall = false,
  forceCorner = false,
  canRemoveCorner = true,
  cornerHint,
  onDeleteWall,
}: {
  selectedWall: number | null;
  selectedVertex: number | null;
  selectedWallLen: number;
  selectedWallLocked: boolean;
  unit: Unit;
  roomPointCount: number;
  onSetWallLength: (len: number) => void;
  onToggleWallLock: () => void;
  onAddCorner: () => void;
  onRemoveVertex: () => void;
  forceWall?: boolean;
  forceCorner?: boolean;
  canRemoveCorner?: boolean;
  cornerHint?: string;
  onDeleteWall?: () => void;
}) {
  if (selectedWall != null || forceWall) {
    return (
      <>
        <div className="px-4 py-4 flex items-center gap-3">
          <NumberStepper
            label="Length"
            value={Math.round(selectedWallLen)}
            onChange={onSetWallLength}
            min={12}
            max={2400}
            step={3}
            suffix="in"
          />
          <div className="flex-1 flex flex-col items-end gap-1.5">
            <div className="text-xs text-ink-400">{formatLength(selectedWallLen, unit)}</div>
            <button
              type="button"
              onClick={onToggleWallLock}
              aria-pressed={selectedWallLocked}
              aria-label={
                selectedWallLocked
                  ? "Unlock this wall's length"
                  : "Lock this wall's length"
              }
              className={cn(
                "inline-flex items-center gap-1 h-7 px-2 rounded-full text-caption font-semibold transition-colors",
                selectedWallLocked
                  ? "bg-accent-600 text-white hover:bg-accent-700"
                  : "bg-ink-100 text-ink-600 hover:bg-ink-200"
              )}
            >
              {selectedWallLocked ? (
                <Lock className="size-3" />
              ) : (
                <Unlock className="size-3" />
              )}
              {selectedWallLocked ? "Locked" : "Lock"}
            </button>
          </div>
        </div>
        {selectedWallLocked && (
          <div className="px-4 pb-3 -mt-1 text-caption text-ink-500">
            Other wall edits will try to preserve this length.
          </div>
        )}
        <div className="border-t border-ink-100 px-3 py-2 flex items-center gap-1.5">
          <Button variant="secondary" size="sm" onClick={onAddCorner}>
            <Plus className="size-4" />
            Add corner
          </Button>
          {onDeleteWall && (
            <Button variant="danger" size="sm" onClick={onDeleteWall}>
              <Trash2 className="size-4" />
              Delete wall
            </Button>
          )}
          <div className="flex-1" />
          <span className="text-caption text-ink-400 px-1">or drag this wall to slide it</span>
        </div>
      </>
    );
  }

  if (selectedVertex != null || forceCorner) {
    return (
      <>
        <div className="px-4 py-4 text-xs text-ink-500">
          Drag the highlighted corner on the canvas to reshape the room. It snaps to neighboring
          walls so corners line up cleanly.
        </div>
        <div className="border-t border-ink-100 px-3 py-2 flex items-center gap-1.5">
          <Button
            variant="danger"
            size="sm"
            onClick={onRemoveVertex}
            disabled={!canRemoveCorner || (selectedVertex != null && roomPointCount <= 3)}
          >
            <Trash2 className="size-4" />
            Remove corner
          </Button>
          <div className="flex-1" />
          <span className="text-caption text-ink-400 px-1">
            {cornerHint ??
              (selectedVertex != null && roomPointCount <= 3 ? "Need at least 3 corners" : "")}
          </span>
        </div>
      </>
    );
  }

  return null;
}
